import { useUser } from "../contexts";
import {
  ApprovalStatus,
  Position,
  PositionImage,
  StatusConst,
  UserOrder,
} from "../interfaces";
import { ImageFile } from "../interfaces";
import useOrder from "./useOrder";

const useOrderPosition = (data?: UserOrder) => {
  const { handleOrderUpdate } = useOrder();
  const [user] = useUser();

  const positionImageAvailable = (order?: UserOrder) => {
    const images = (order ? order.positions : user.order.positions).filter(
      (item) => {
        if (item.positionImage && item.positionImage?.length > 0) {
          return item.positionImage;
        }
      },
    );
    if (images.length > 0) {
      return true;
    }

    return false;
  };

  const dontApproveImage = async (
    rejectedDescription: string,
    rejectedReason: string,
    files: ImageFile[],
    current: PositionImage | null,
    item: Position,
    order: UserOrder,
  ) => {
    const positionImage: PositionImage[] = [...(item?.positionImage || [])].map(
      (image) => {
        if (image.key === current?.key) {
          return {
            ...current,
            approvalStatus: ApprovalStatus.No,
            acceptedDescription: "",
            rejectedReason,
            rejectedDescription,
            fullName: "",
            files,
          };
        }
        return image;
      },
    );
    const positions = order.positions.map((position) => {
      if (
        position.articleNumber === item.articleNumber &&
        position.heightByMm === item.heightByMm &&
        position.widthByMm === item.widthByMm
      ) {
        return { ...position, positionImage };
      }
      return position;
    });

    return await handleOrderUpdate({
      id: order.id,
      status: StatusConst.Error,
      positions,
    });
  };

  // Decline all uploaded images
  const declineAllImage = async (
    rejectedDescription: string,
    rejectedReason: string,
    files: ImageFile[],
  ) => {
    if (!data) return;
    const positions: Position[] = [];
    data?.positions.map(async (item: Position) => {
      const positionImage: PositionImage[] =
        [...(item?.positionImage || [])].map((image) => {
          return {
            ...image,
            approvalStatus: ApprovalStatus.No,
            acceptedDescription: "",
            rejectedReason,
            rejectedDescription,
            fullName: "",
            files,
          };
        }) || [];
      positions.push({ ...item, positionImage });
    });

    const hasError = positions.find((position) =>
      position.positionImage?.find(
        (positionImage) => positionImage.approvalStatus === ApprovalStatus.No,
      ),
    );
    const status = hasError && { status: StatusConst.Error };
    return handleOrderUpdate({
      id: data?.id,
      ...status,
      positions,
    });
  };

  const approveImage = async (
    acceptedDescription: string = "",
    current: PositionImage | null,
    item: Position,
    order: UserOrder,
  ) => {
    const positionImage: PositionImage[] = [...(item?.positionImage || [])].map(
      (image) => {
        if (image.key === current?.key) {
          return {
            ...current,
            approvalStatus: ApprovalStatus.Yes,
            acceptedDescription: acceptedDescription || "",
            comment: image.comment,
            rejectedReason: "",
            rejectedDescription: "",
          };
        }
        return image;
      },
    );
    const positions = order.positions.map((position) => {
      if (
        position.articleNumber === item.articleNumber &&
        position.heightByMm === item.heightByMm &&
        position.widthByMm === item.widthByMm
      ) {
        return { ...position, positionImage };
      }
      return {
        ...position,
        positionImage: position.positionImage ? position.positionImage : [],
      };
    });

    const hasError = positions.find((position) =>
      position.positionImage.find(
        (positionImage) => positionImage.approvalStatus === ApprovalStatus.No,
      ),
    );

    const status = hasError
      ? { status: StatusConst.Error }
      : { status: StatusConst.Uploaded };

    if (!data) return;
    return await handleOrderUpdate({
      id: data.id,
      ...status,
      positions,
    });
  };

  // Accept all uploaded images
  const approveAllImage = async (acceptedDescription: string = "") => {
    if (!data) return;
    const positions: Position[] = [];
    let hasError: PositionImage | undefined;
    data?.positions.map(async (item: Position) => {
      const positionImage: PositionImage[] =
        [...(item?.positionImage || [])].map((image) => {
          return {
            ...image,
            acceptedDescription,
            approvalStatus: ApprovalStatus.Yes,
            comment: image.comment,
            rejectedReason: "",
            rejectedDescription: "",
          };
        }) || [];
      positions.push({ ...item, positionImage });
      hasError = item.positionImage?.find(
        (positionImage) => positionImage.approvalStatus === ApprovalStatus.No,
      );
    });
    const status = hasError
      ? { status: StatusConst.Error }
      : { status: StatusConst.Uploaded };
    return handleOrderUpdate({
      id: data?.id,
      ...status,
      positions,
    });
  };

  return {
    positionImageAvailable,
    declineAllImage,
    dontApproveImage,
    approveAllImage,
    approveImage,
  };
};

export default useOrderPosition;
